import React, { createContext, useContext, useState, useEffect } from 'react';
import { fetchData, createData, updateData, deleteData } from '../utils/apiClient';

// Token refresh utility
const checkAndRefreshToken = async () => {
  const expiry = localStorage.getItem('peart_auth_token_expiry');
  if (!expiry) return;
  
  const now = Date.now();
  const timeUntilExpiry = parseInt(expiry) - now;
  
  // Wenn Token in weniger als 5 Minuten abläuft, refreshen
  if (timeUntilExpiry < 5 * 60 * 1000) {
    console.log('Token expires soon, attempting refresh...');
    if (window.oidcAuth?.isAuthenticated) {
      try {
        await window.oidcAuth.signinSilent();
        console.log('Token refreshed successfully');
      } catch (error) {
        console.error('Failed to refresh token:', error);
      }
    }
  }
};

const API_URL = import.meta.env.VITE_API_URL;

const AdminContext = createContext();

export function AdminProvider({ children }) {
  const [series, setSeries] = useState([]);
  const [colors, setColors] = useState([]);
  const [awards, setAwards] = useState([]);
  const [exhibitions, setExhibitions] = useState([]);
  const [paintings, setPaintings] = useState([]);
  const [news, setNews] = useState([]);
  const [home, setHome] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchAllData = async () => {
      try {
        setLoading(true);
        const [seriesData, colorsData, awardsData, exhibitionsData, paintingsData, newsData, homeData] = await Promise.all([
          fetchData('series'),
          fetchData('colors'),
          fetchData('awards'),
          fetchData('exhibitions'),
          fetchData('paintings'),
          fetchData('news'),
          fetchData('home')
        ]);
        setSeries(Array.isArray(seriesData) ? seriesData : []);
        setColors(Array.isArray(colorsData) ? colorsData : []);
        setAwards(Array.isArray(awardsData) ? awardsData : []);
        setExhibitions(Array.isArray(exhibitionsData) ? exhibitionsData : []);
        setPaintings(Array.isArray(paintingsData) ? paintingsData : []);
        setNews(Array.isArray(newsData) ? newsData : []);
        setHome(homeData);
        setError(null);
      } catch (err) {
        setSeries([]);
        setColors([]);
        setAwards([]);
        setExhibitions([]);
        setPaintings([]);
        setNews([]);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchAllData();
  }, []);

  const handleCreate = async (type, data) => {
    try {
      const newData = await createData(type, data);
      switch (type) {
        case 'series':
          // Refresh entire list to get correct sort_order values
          const updatedSeries = await fetchData('series');
          setSeries(Array.isArray(updatedSeries) ? updatedSeries : []);
          break;
        case 'colors':
          // Refresh entire list to get correct sort_order values
          const updatedColors = await fetchData('colors');
          setColors(Array.isArray(updatedColors) ? updatedColors : []);
          break;
        case 'awards':
          // Refresh entire list to get correct sort_order values
          const updatedAwards = await fetchData('awards');
          setAwards(Array.isArray(updatedAwards) ? updatedAwards : []);
          break;
        case 'exhibitions':
          // Refresh entire list to get correct sort_order values
          const updatedExhibitions = await fetchData('exhibitions');
          setExhibitions(Array.isArray(updatedExhibitions) ? updatedExhibitions : []);
          break;
        case 'paintings':
          setPaintings(prev => [newData, ...prev]); // New paintings at top
          break;
        case 'news':
          // News backend returns {message: "..."} instead of full object
          // So we need to refresh the list like awards/exhibitions
          // Check token before post-upload refresh to prevent logout
          await checkAndRefreshToken();
          const updatedNews = await fetchData('news');
          // Extra token check after loading large dataset
          await checkAndRefreshToken();
          setNews(Array.isArray(updatedNews) ? updatedNews : []);
          break;
        case 'home':
          setHome(newData);
          break;
        default:
          break;
      }
      return newData;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  const handleUpdate = async (type, id, data) => {
    try {
      const updatedData = await updateData(type, id, data);
      
      // First update the specific data type
      switch (type) {
        case 'series':
          setSeries(prev => prev.map(item => item.id === id ? updatedData : item));
          break;
        case 'colors':
          setColors(prev => prev.map(item => item.id === id ? updatedData : item));
          break;
        case 'awards':
          setAwards(prev => prev.map(item => item.id === id ? updatedData : item));
          break;
        case 'exhibitions':
          setExhibitions(prev => prev.map(item => item.id === id ? updatedData : item));
          break;
        case 'paintings':
          setPaintings(prev => prev.map(item => item.id === id ? updatedData : item));
          break;
        case 'news':
          // News backend might return {message: "..."} instead of full object
          // So refresh the list to be safe
          // Check token before post-upload refresh to prevent logout
          await checkAndRefreshToken();
          const refreshedNews = await fetchData('news');
          setNews(Array.isArray(refreshedNews) ? refreshedNews : []);
          break;
        case 'home':
          setHome(updatedData);
          break;
        default:
          break;
      }

      // Then refresh all related data
      const refreshData = async () => {
        const [updatedPaintings, updatedSeries, updatedColors, updatedAwards, updatedExhibitions] = await Promise.all([
          fetchData('paintings'),
          fetchData('series'),
          fetchData('colors'),
          fetchData('awards'),
          fetchData('exhibitions')
        ]);

        setPaintings(Array.isArray(updatedPaintings) ? updatedPaintings : []);
        setSeries(Array.isArray(updatedSeries) ? updatedSeries : []);
        setColors(Array.isArray(updatedColors) ? updatedColors : []);
        setAwards(Array.isArray(updatedAwards) ? updatedAwards : []);
        setExhibitions(Array.isArray(updatedExhibitions) ? updatedExhibitions : []);
      };

      await refreshData();
      return updatedData;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  const handleDelete = async (type, id) => {
    try {
      await deleteData(type, id);
      switch (type) {
        case 'series':
          // Refresh entire list to get correct sort_order values after deletion
          const updatedSeries = await fetchData('series');
          setSeries(Array.isArray(updatedSeries) ? updatedSeries : []);
          break;
        case 'colors':
          // Refresh entire list to get correct sort_order values after deletion
          const updatedColors = await fetchData('colors');
          setColors(Array.isArray(updatedColors) ? updatedColors : []);
          break;
        case 'awards':
          // Refresh entire list to get correct sort_order values after deletion
          const updatedAwards = await fetchData('awards');
          setAwards(Array.isArray(updatedAwards) ? updatedAwards : []);
          break;
        case 'exhibitions':
          // Refresh entire list to get correct sort_order values after deletion
          const updatedExhibitions = await fetchData('exhibitions');
          setExhibitions(Array.isArray(updatedExhibitions) ? updatedExhibitions : []);
          break;
        case 'paintings':
          setPaintings(prev => prev.filter(item => item.id !== id));
          break;
        case 'news':
          // Refresh entire list to get correct sort_order values after deletion
          // Check token before refresh to prevent logout
          await checkAndRefreshToken();
          const updatedNews = await fetchData('news');
          setNews(Array.isArray(updatedNews) ? updatedNews : []);
          break;
        default:
          break;
      }
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  // Generic helper function to reorder items optimistically
  const reorderItems = (itemsList, itemId, newPosition) => {
    const updatedItems = [...itemsList];
    const itemIndex = updatedItems.findIndex(item => item.id === itemId);
    
    if (itemIndex === -1) return updatedItems;
    
    const item = updatedItems[itemIndex];
    const oldPosition = item.sort_order;
    
    // Update positions
    updatedItems.forEach(item => {
      if (item.id === itemId) {
        item.sort_order = newPosition;
      } else if (newPosition < oldPosition && item.sort_order >= newPosition && item.sort_order < oldPosition) {
        item.sort_order += 1;
      } else if (newPosition > oldPosition && item.sort_order > oldPosition && item.sort_order <= newPosition) {
        item.sort_order -= 1;
      }
    });
    
    // Sort by new positions
    return updatedItems.sort((a, b) => a.sort_order - b.sort_order);
  };

  // Legacy helper for backwards compatibility
  const reorderPaintings = (paintingsList, paintingId, newPosition) => {
    return reorderItems(paintingsList, paintingId, newPosition);
  };

  // Generic position update handler
  const createPositionUpdateHandler = (entityType, items, setItems) => {
    return async (itemId, newPosition) => {
      const originalItems = [...items];
      
      try {
        // Optimistic update
        const optimisticItems = reorderItems(items, itemId, newPosition);
        setItems(optimisticItems);
        
        // API Call
        const token = localStorage.getItem('peart_auth_token');
        const response = await fetch(`${API_URL}/${entityType}/${itemId}/position`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ position: parseInt(newPosition) })
        });

        if (!response.ok) throw new Error(`Failed to update ${entityType} position`);
        
      } catch (error) {
        console.error(`Error updating ${entityType} position:`, error);
        // Revert optimistic update on error
        setItems(originalItems);
        // Refresh from server to ensure consistency
        try {
          const refreshedItems = await fetchData(entityType);
          setItems(Array.isArray(refreshedItems) ? refreshedItems : []);
        } catch (refreshError) {
          console.error(`Error refreshing ${entityType}:`, refreshError);
        }
        throw error;
      }
    };
  };

  // Handle position updates for paintings (legacy)
  const handlePositionUpdate = createPositionUpdateHandler('paintings', paintings, setPaintings);
  
  // Handle position updates for all entities
  const handleExhibitionPositionUpdate = createPositionUpdateHandler('exhibitions', exhibitions, setExhibitions);
  const handleAwardPositionUpdate = createPositionUpdateHandler('awards', awards, setAwards);
  const handleNewsPositionUpdate = createPositionUpdateHandler('news', news, setNews);
  const handleColorPositionUpdate = createPositionUpdateHandler('colors', colors, setColors);
  const handleSeriesPositionUpdate = createPositionUpdateHandler('series', series, setSeries);

  const value = {
    series,
    colors,
    awards,
    exhibitions,
    paintings,
    news,
    home,
    loading,
    error,
    handleCreate,
    handleUpdate,
    handleDelete,
    handlePositionUpdate,
    handleExhibitionPositionUpdate,
    handleAwardPositionUpdate,
    handleNewsPositionUpdate,
    handleColorPositionUpdate,
    handleSeriesPositionUpdate,
    fetchData
  };

  return (
    <AdminContext.Provider value={value}>
      {children}
    </AdminContext.Provider>
  );
}

// Export useAdmin as a named export
export const useAdmin = () => {
  const context = useContext(AdminContext);
  if (!context) {
    throw new Error('useAdmin must be used within an AdminProvider');
  }
  return context;
}; 